/** @jsx jsx */
import {ChangeEvent, Fragment, useEffect, useState} from 'react'
import {jsx} from 'theme-ui'
import {FamilyBasketMetadata, Metadata} from './shared'
import {useTranslation} from 'react-i18next'
import CustomSelect, {ISelectOption} from './CustomSelect'

interface IFamilyBasket {
  amount: number | null
  onAmountChange: (
    amount: number | null,
    userInput: null | 'custom' | 'fixed',
  ) => void
  metadata: Metadata | null
  setMetadata: (metadata: FamilyBasketMetadata) => void
  options: {label: string; price: number; country: string; disabled?: boolean}[]
}

const buildMetadata = ({
  quantity = 1,
  unit_price = 70,
  country = 'Morocco',
}: {
  quantity: number
  unit_price: number
  country: string
}): FamilyBasketMetadata => {
  return {
    type: 'Family basket',
    country,
    unit_price,
    quantity,
  }
}

const FamilyBasket: React.FC<IFamilyBasket> = ({
  amount,
  onAmountChange,
  metadata,
  setMetadata,
  options,
}) => {
  const {t} = useTranslation()
  const [selectedOption, setSelectedOption] = useState(
    options.find((o) => o.country === metadata?.country && !o.disabled) ||
      options.find((o) => !o.disabled) ||
      options[0],
  )
  const defaultOption =
    options.find((o) => !o.disabled) || options[0]
  useEffect(() => {
    if (!metadata) {
      setMetadata(
        buildMetadata({
          quantity: 1,
          unit_price: defaultOption.price,
          country: defaultOption.country,
        }),
      )
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (metadata && amount !== metadata.unit_price * metadata.quantity) {
      onAmountChange(metadata.unit_price * metadata.quantity, 'fixed')
    }
  }, [metadata]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeSelection = (option: ISelectOption) => {
    const userOption = options.find((o) => o.country === option.value)
    if (!userOption) {
      return
    }
    setSelectedOption(userOption)
    setMetadata(
      buildMetadata({
        quantity: 1,
        unit_price: userOption.price,
        country: option.value,
      }),
    )
  }

  const handleChangeQuantity = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value
    const quantity = parseInt(value, 10) || 0
    setMetadata(
      buildMetadata({
        quantity,
        unit_price: selectedOption.price,
        country: selectedOption.country,
      }),
    )
  }
  const displayedQuantity = metadata ? metadata.quantity + '' : ''
  return (
    <div
      sx={{
        py: 16,
        px: 13,
      }}
    >
      {options.length > 1 ? (
        <CustomSelect
          options={options.map((option) => ({
            label: (
              <div>
                {option.label} - {option.price} USD
                {option.disabled && (
                  <div
                    sx={{
                      fontSize: 11,
                      bg: 'primary',
                      color: 'white',
                      display: 'inline-block',
                      px: 1,
                      mt: '2px',
                    }}
                  >
                    {t('Limit reached')}
                  </div>
                )}
              </div>
            ),
            value: option.country,
            isDisabled: option.disabled === true,
          }))}
          value={{
            label: `${selectedOption.label} - ${selectedOption.price} USD`,
            value: selectedOption.country,
          }}
          onChange={handleChangeSelection}
        />
      ) : (
        <div
          sx={{
            bg: '#fff',
            borderRadius: 4,
            height: 46,
            display: 'flex',
            alignItems: 'center',
            px: 12,
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.07)',
            cursor: 'not-allowed',
          }}
        >
          {selectedOption.label} - {selectedOption.price} USD
          {selectedOption.disabled && (
            <div
              sx={{
                fontSize: 11,
                bg: 'primary',
                color: 'white',
                display: 'inline-block',
                px: 1,
                mt: '2px',
              }}
            >
              {t('Limit reached')}
            </div>
          )}
        </div>
      )}

      <div
        sx={{
          display: 'flex',
          mt: 14,
          justifyContent: 'space-between',
        }}
      >
        <div
          sx={{
            width: [140, , 152],
            height: 46,
            position: 'relative',
          }}
        >
          <input
            type="number"
            sx={{
              width: '100%',
              height: '100%',
              border: '1px solid #DEE3E3',
              borderRadius: 4,
              px: 15,
              pl: 72,
              fontSize: 13,
              outline: 0,
              '::placeholder': {
                color: '#717A7B',
              },
              '&:focus': {
                border: '1px solid #055064',
              },
            }}
            value={displayedQuantity || 1}
            onChange={handleChangeQuantity}
          />
          <span
            sx={{
              color: '#949FA0',
              fontSize: 13,
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            {t('Quantity')}:
          </span>
        </div>
        <div
          sx={{
            width: [140, , 152],
            bg: '#fff',
            borderRadius: 4,
            height: 46,
            display: 'flex',
            alignItems: 'center',
            px: 12,
            border: '1px solid #DEE3E3',
            cursor: 'not-allowed',
          }}
        >
          <span sx={{color: '#949FA0', fontSize: 13}}>{t('Total')}:</span>
          <span>&nbsp;{amount} USD</span>
        </div>
      </div>
    </div>
  )
}

export default FamilyBasket
